import React, { useState } from "react";
import { Tooltip } from "./Tooltip";

// Dynamically import PDF generator to avoid development issues
const generateCVPDF = async () => {
  try {
    const { generateCVPDF: pdfGenerator } = await import("../services/pdfGenerator");
    return pdfGenerator();
  } catch (error) {
    console.error('Error loading PDF generator:', error);
    alert('PDF generation is not available in development mode');
  }
};

// Skills data - keeping it synchronized with Services.jsx
const skillsData = {
  administrative: [
    "ClickUp", "Trello", "Asana", "Google Suite", "MS Teams", 
    "Photoshop", "Illustrator", "Adobe XD", "Figma", "Slack",
    "Technical Leadership", "Project Management", "Client Communication", 
    "Team Leadership", "Technical Audits", "Constant Contact", 
    "Email Marketing", "Campaign Templates", "Soho"
  ],
  technical: [
    "WordPress", "React.js", "JavaScript", "PHP", "Node.js", 
    "CSS/SCSS", "MySQL", "WooCommerce", "Shopify", "Shopify Plus", "Liquid", "HeadLess", 
    "Tailwind", "HTML", "Bootstrap", "Core Web Vitals", "Elementor", "Next.js"
  ],
  devTools: [
    "GitHub", "VS Code", "Copilot", "Git", "Docker", 
    "Linux", "WPEngine", "CPanel", "WHM", "SFTP", "GoDaddy", "MailChimp"
  ]
};

const SkillsPills = ({ darkTheme }) => {
  return (
    <div className="skills-pills">
      <style>{`
        .skill-pill {
          border: none;
          transition: all 0.3s ease;
          cursor: default;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          font-weight: 500;
          color: white !important;
          font-size: 11px;
          margin: 2px;
          display: inline-block;
        }
        .skill-pill:hover {
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(0,0,0,0.2);
        }
        .skill-pill.administrative {
          background-color: #6610f2;
        }
        .skill-pill.technical {
          background-color: #0ea075;
        }
        .skill-pill.dev-tools {
          background-color: #6c757d;
        }
      `}</style>
      
      {/* Administrative Skills */}
      {skillsData.administrative.map((skill, index) => (
        <span key={`admin-${index}`} className="skill-pill administrative px-2 py-1 rounded-pill">
          {skill}
        </span>
      ))}
      
      {/* Technical Skills */}
      {skillsData.technical.map((skill, index) => (
        <span key={`tech-${index}`} className="skill-pill technical px-2 py-1 rounded-pill">
          {skill}
        </span>
      ))}
      
      {/* Development Tools */}
      {skillsData.devTools.map((skill, index) => (
        <span key={`dev-${index}`} className="skill-pill dev-tools px-2 py-1 rounded-pill">
          {skill}
        </span>
      ))}
    </div>
  );
};

const CVLayout = ({ darkTheme, toggleDarkMode }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleExportPDF = async () => {
    try {
      setIsGenerating(true);
      await generateCVPDF();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={`cv-layout ${darkTheme ? "dark-theme" : "light-theme"}`} id="cv-content">
      {/* PDF Export Button */}
      <div className="pdf-controls d-print-none">
        <button
          onClick={handleExportPDF}
          disabled={isGenerating}
          className="btn btn-sm position-fixed"
          style={{ 
            top: '20px', 
            right: '20px', 
            zIndex: 1000,
            backgroundColor: '#0ea075',
            borderColor: '#0ea075',
            color: 'white'
          }}
        >
          <i className={`fas ${isGenerating ? 'fa-spinner fa-spin' : 'fa-file-pdf'} me-2`}></i>
          {isGenerating ? 'Generating...' : 'Export PDF'}
        </button>
      </div>
      {/* Sidebar */}
      <aside className={`cv-sidebar ${darkTheme ? "bg-dark" : "bg-light"}`}>
        {/* Profile Section */}
        <div className="profile-section text-center p-4">
          <div className="profile-image-container mb-3">
            <img
              src="/images/fachu-dev-profile-grad.jpg"
              alt="Profile"
              className="profile-image rounded-circle"
              loading="eager"
              decoding="sync"
            />
          </div>
          <h1 className={`h4 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
            Facundo Criscuolo
          </h1>
          <p className={`text-muted mb-3 ${darkTheme ? "text-light" : ""}`}>
            Tech/IT Consultant
          </p>
          
          {/* Contact Info */}
          <div className="contact-info text-start">
            <div className={`contact-item mb-2 ${darkTheme ? "text-light" : ""}`}>
              <i className="fas fa-envelope me-2" style={{ color: '#0ea075' }}></i>
              <span className="small">criscuolo.facu@gmail.com</span>
            </div>
            <div className={`contact-item mb-2 ${darkTheme ? "text-light" : ""}`}>
              <i className="fab fa-whatsapp me-2" style={{ color: '#0ea075' }}></i>
              <span className="small">(+39) 389 034-1230</span>
            </div>
            <div className={`contact-item mb-2 ${darkTheme ? "text-light" : ""}`}>
              <i className="fas fa-phone me-2" style={{ color: '#0ea075' }}></i>
              <span className="small">(+54) 117075-1022</span>
            </div>
            <div className={`contact-item mb-3 ${darkTheme ? "text-light" : ""}`}>
              <i className="fas fa-map-marker-alt me-2" style={{ color: '#0ea075' }}></i>
              <span className="small">Buenos Aires, Argentina</span>
            </div>
          </div>

          {/* Social Icons */}
          <ul className="social-icons social-icons-sm justify-content-center mb-4">
            <li className="social-icons-github">
              <Tooltip text="Github" placement="top">
                <a
                  href="https://github.com/fachu-dev"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-github" />
                </a>
              </Tooltip>
            </li>
            <li className="social-icons-linkedin">
              <Tooltip text="LinkedIn" placement="top">
                <a
                  href="https://www.linkedin.com/in/facundo-criscuolo"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin" />
                </a>
              </Tooltip>
            </li>
          </ul>
        </div>

        {/* Skills Section */}
        <div className="skills-section p-4">
          <h3 className={`h6 mb-3 text-uppercase ${darkTheme ? "text-white" : "text-dark"}`}>
            Skills
          </h3>
          <SkillsPills darkTheme={darkTheme} />
        </div>

        {/* Languages Section */}
        <div className="languages-section p-4">
          <h3 className={`h6 mb-3 text-uppercase ${darkTheme ? "text-white" : "text-dark"}`}>
            Languages
          </h3>
          <div className="languages-list">
            <div className={`language-item d-flex align-items-center mb-2 ${darkTheme ? "text-light" : "text-dark"}`}>
              <span className="flag-icon me-2">🇪🇸</span>
              <span className="small">Spanish</span>
            </div>
            <div className={`language-item d-flex align-items-center mb-2 ${darkTheme ? "text-light" : "text-dark"}`}>
              <span className="flag-icon me-2">🇺🇸</span>
              <span className="small">English</span>
            </div>
            <div className={`language-item d-flex align-items-center mb-0 ${darkTheme ? "text-light" : "text-dark"}`}>
              <span className="flag-icon me-2">🇮🇹</span>
              <span className="small">Italian</span>
            </div>
          </div>
        </div>

        {/* Dark Mode Toggle */}
        <div className="dark-mode-toggle p-4 mt-auto d-print-none">
          <button
            onClick={toggleDarkMode}
            className={`btn btn-outline-${darkTheme ? 'light' : 'dark'} btn-sm w-100 d-print-none`}
            aria-label="Toggle dark mode"
          >
            <i className={`fa${darkTheme ? 's fa-sun' : 'r fa-moon'} me-2`}></i>
            {darkTheme ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`cv-main-content ${darkTheme ? "bg-dark-1" : "bg-light"}`}>
        <div className="content-sections p-4 p-lg-5">
          
          {/* Professional Summary */}
          <section className="cv-section mb-5">
            <h2 className={`section-title h4 mb-3 ${darkTheme ? "text-white" : "text-dark"}`}>
              Professional Summary
            </h2>
            <div className={`section-content ${darkTheme ? "text-light" : "text-dark"}`}>
              <p className="mb-3">
                I'm Facundo Criscuolo, an independent IT and Web Development Consultant based in Argentina, specialized in WordPress, hosting infrastructure, and AI-driven workflow automation.
              </p>
              <p className="mb-3">
                Over the past years, I've helped businesses, agencies, and organizations build, manage, and optimize their online platforms, always focusing on performance, usability, and long-term scalability.
              </p>
              <p className="mb-0">
                My work combines hands-on technical expertise with AI-powered tools, covering every stage from architecture and implementation to optimization and long-term support, making technology more efficient, accessible, and truly useful for every project I work on.
              </p>
            </div>
          </section>

          {/* Experience */}
          <section className="cv-section mb-5">
            <h2 className={`section-title h4 mb-4 ${darkTheme ? "text-white" : "text-dark"}`}>
              Professional Experience
            </h2>
            <div className="section-content">
              
              {/* Web Development Lead - Roar Media */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Web Development Lead
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Roar Media</span>
                      <span className="ms-2">🇺🇸</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    Mar 2026 - Present
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Leading the web development team across WordPress, Shopify and projects from scratch, ensuring high-quality technical delivery and alignment with business goals</li>
                  <li>Ownership of project planning, technical estimations, workflow organization, and cross-functional collaboration with design and marketing teams</li>
                  <li>Direct client communication for technical discovery, project updates, issue resolution, and strategic web recommendations</li>
                  <li>Oversight of website performance, Core Web Vitals optimization, hosting infrastructure, security, and scalability across multiple accounts</li>
                  <li>Management of internal development standards, QA processes, documentation, and continuous improvement initiatives</li>
                  <li>Mentoring and technical guidance for junior and mid-level developers, supporting team growth and delivery consistency</li>
                </ul>
                <div className="tech-stack">
                  <div className="d-flex flex-wrap gap-1">
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WordPress</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Shopify</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Shopify Plus</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Core Web Vitals</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>PHP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>JavaScript</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GitHub</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WP Engine</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>VS Code</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Technical Leadership</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Project Management</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Client Communication</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Team Leadership</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>ClickUp</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Technical Audits</span>
                  </div>
                </div>
              </div>

              {/* Senior Web Developer - Roar Media */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Senior Web Developer
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Roar Media</span>
                      <span className="ms-2">🇺🇸</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    Mar 2024 - Mar 2026
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Responsible for technical leadership and development of web platforms built with WordPress and Shopify</li>
                  <li>Manage hosting on WP Engine, security, performance optimization, and ongoing maintenance across multiple accounts</li>
                  <li>Development of custom solutions (plugins and themes), technical audits, and internal technical support</li>
                  <li>Expert in translating high-fidelity designs from Figma and Adobe XD into pixel-perfect, production-ready websites</li>
                  <li>Direct client collaboration, integrating digital marketing tasks and continuous improvement processes</li>
                </ul>
                <div className="tech-stack">
                  <div className="d-flex flex-wrap gap-1">
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WordPress</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WooCommerce</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Shopify</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Shopify Plus</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Elementor</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>HTML5</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>CSS</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>JavaScript</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>PHP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Next.js</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Tailwind CSS</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GitHub</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WP Engine</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GoDaddy</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>SFTP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>Adobe XD</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Team Leadership</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>ClickUp</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>MailChimp</span>
                  </div>
                </div>
              </div>

              {/* Web Developer - Roar Media */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Web Developer
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Roar Media</span>
                      <span className="ms-2">🇺🇸</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    Jun 2022 - Mar 2024
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Development and maintenance of WordPress websites, including custom themes, plugins, and page builder implementations</li>
                  <li>Management of hosting environments, domains, DNS settings, migrations, and ongoing technical support across multiple client accounts</li>
                  <li>Front-end development using HTML, CSS, JavaScript, and responsive design best practices</li>
                  <li>Implementation of website updates, landing pages, and content changes based on client and internal team requirements</li>
                  <li>Creation, customization, and deployment of email marketing templates and campaign layouts across multiple platforms</li>
                  <li>Configuration and support of email campaigns, audience segmentation, automation flows, and template troubleshooting</li>
                  <li>Performance optimization, bug fixing, troubleshooting, and plugin compatibility support</li>
                  <li>Collaboration with designers and marketing teams to translate layouts into production-ready websites and campaign assets</li>
                </ul>
                <div className="tech-stack">
                  <div className="d-flex flex-wrap gap-1">
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WordPress</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Elementor</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>HTML</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>CSS</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>JavaScript</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>PHP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WooCommerce</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GitHub</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WPEngine</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GoDaddy</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>SFTP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>VS Code</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>MailChimp</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Constant Contact</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Email Marketing</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Campaign Templates</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Soho</span>
                  </div>
                </div>
              </div>

              {/* Creative Director / Web Developer - Motus Creativo */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Creative Director / Web Developer
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Motus Creativo</span>
                      <span className="ms-2">🇦🇷</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    2023 - Present
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Led the creative and technical direction of the web department, ensuring high quality standards across all digital projects</li>
                  <li>Responsible for hosting management and technical support across all client accounts</li>
                  <li>Development and maintenance of WordPress and Ecommerce websites with a strong focus on performance, security, and scalability</li>
                  <li>Oversaw creative quality, optimized workflows and processes, and collaborated directly with clients to deliver high-quality digital solutions</li>
                </ul>
                <div className="tech-stack">
                  <div className="d-flex flex-wrap gap-1">
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WordPress</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WooCommerce</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>ACF</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Elementor</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>PHP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>HTML</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>CSS</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>JavaScript</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>Node.js</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GitHub</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WHM</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>CPanel</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>GoDaddy</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>SFTP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>VS Code</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>Copilot</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>ChatGPT</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Slack</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>ClickUp</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Google Suite</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Mailchimp</span>
                  </div>
                </div>
              </div>

              {/* WordPress Developer - Dimensiona */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      WordPress & PHP Developer
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Dimensiona</span>
                      <span className="ms-2">🇪🇸</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    2021
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Worked as a WordPress Developer delivering websites for public and government institutions, operating under strict security, compliance, and deployment processes</li>
                  <li>Gained strong experience navigating bureaucratic and formal approval workflows typical of public-sector projects</li>
                  <li>Responsible for custom WordPress development (themes and plugins), PHP development, hosting management, Linux-based deployments via terminal</li>
                  <li>Ongoing maintenance with a strong focus on security and stability across multiple sites</li>
                </ul>
                <div className="tech-stack">
                  <div className="d-flex flex-wrap gap-1">
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>WordPress</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>PHP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>HTML</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>CSS</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>Linux</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WHM</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>SFTP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>FTP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>SSH</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Trello</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Google Suite</span>
                  </div>
                </div>
              </div>

              {/* IT Infrastructure & Web Development Specialist - Sermex S.A. */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      IT Infrastructure & Web Development Specialist
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Sermex S.A.</span>
                      <span className="ms-2">🇦🇷</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    2009 - 2022
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Built a strong technical foundation over 12 years managing IT infrastructure and nationwide technical support for a healthcare organization serving more than 300 employees across Argentina</li>
                  <li>Responsible for server maintenance, corporate email services, VPN administration, IP telephony, and support for core internal systems, including authorization and HR platforms</li>
                  <li>In parallel, evolved into a web-focused role, leading the development, hosting, and maintenance of multiple corporate websites for different business units</li>
                  <li>Worked with enterprise-level tools and strict operational requirements typical of the healthcare sector</li>
                </ul>
                <div className="tech-stack">
                  <div className="d-flex flex-wrap gap-1">
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>PHP</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>HTML</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>CSS</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white', fontSize: '0.7rem' }}>MySQL</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WAN</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>VPN</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>MS Exchange</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>SharePoint</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>pfSense</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>Asterisk</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>ZK</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>WHM</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>CPanel</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.7rem' }}>Linux</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>MS Teams</span>
                    <span className="badge me-1 mb-1" style={{ backgroundColor: '#6610f2', color: 'white', fontSize: '0.7rem' }}>Tango</span>
                  </div>
                </div>
              </div>

              {/* IT Support Technician - Worftech */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      IT Support Technician
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>Worftech</span>
                      <span className="ms-2">🇦🇷</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    2007
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>First professional experience in a retail IT environment, providing hands-on computer assembly, repair, and network setup</li>
                  <li>Delivered technical support to individual users and small business clients, both on-site and in-store</li>
                  <li>Developed strong problem-solving, customer service, and communication skills, along with experience in sales, online and in-person transactions, and basic financial operations</li>
                </ul>
              </div>

              {/* Placeholder for additional experience items */}
              {/* Add more experience items as needed */}
            </div>
          </section>

          {/* Personal Projects */}
          <section className="cv-section mb-5">
            <h2 className={`section-title h4 mb-4 ${darkTheme ? "text-white" : "text-dark"}`}>
              Personal Projects
            </h2>
            <div className="section-content">

              {/* fachu.dev */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Founder & IT Consultant
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>fachu.dev</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    2025 - Present
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Established a comprehensive IT consultancy, providing a wide range of services, including custom web and software development, reseller hosting, and cloud solutions</li>
                  <li>Offer expert IT consulting to agencies and organizations, delivering tailored solutions in development, infrastructure, and ongoing support</li>
                  <li>Trusted partner for diverse IT needs, from strategic guidance to hands-on development and technical operations</li>
                </ul>
              </div>

              {/* oasys .tec */}
              <div className="experience-item mb-5">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Co-Founder & Web Developer
                    </h3>
                    <p className="mb-1 fw-500 d-flex align-items-center" style={{ color: '#0ea075' }}>
                      <span>oasys .tec</span>
                    </p>
                  </div>
                  <span className={`badge ${darkTheme ? "bg-secondary" : "bg-light text-dark"} ms-2`}>
                    2014 - 2022
                  </span>
                </div>
                <ul className={`small mb-3 ${darkTheme ? "text-light" : "text-muted"}`}>
                  <li>Co-founded a digital marketing and web services agency focused on supporting small startups</li>
                  <li>Delivered end-to-end web solutions, including website development, hosting services, and general technical support</li>
                  <li>Collaborated on integrated marketing offerings while ensuring high-quality web delivery tailored to early-stage businesses</li>
                </ul>
              </div>

            </div>
          </section>

          {/* Education */}
          <section className="cv-section mb-5">
            <h2 className={`section-title h4 mb-4 ${darkTheme ? "text-white" : "text-dark"}`}>
              Education & Certifications
            </h2>
            <div className="section-content">
              
              {/* ReactJS Course */}
              <div className="education-item mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      ReactJS - Course
                    </h3>
                    <p className={`small mb-0 ${darkTheme ? "text-light" : "text-muted"}`}>
                      CoderHouse - 2023
                    </p>
                  </div>
                </div>
              </div>

              {/* Javascript Course */}
              <div className="education-item mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Javascript - Course
                    </h3>
                    <p className={`small mb-0 ${darkTheme ? "text-light" : "text-muted"}`}>
                      CoderHouse - 2022
                    </p>
                  </div>
                </div>
              </div>

              {/* System Analyst */}
              <div className="education-item mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      System Analyst - Higher Education Degree
                    </h3>
                    <p className={`small mb-0 ${darkTheme ? "text-light" : "text-muted"}`}>
                      Escuela Da Vinci - 2014
                    </p>
                  </div>
                </div>
              </div>

              {/* Networks and Operating Systems Technician */}
              <div className="education-item mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Networks & Operating Systems Technician - Higher Education Degree
                    </h3>
                    <p className={`small mb-0 ${darkTheme ? "text-light" : "text-muted"}`}>
                      CTEC - 2009
                    </p>
                  </div>
                </div>
              </div>

              {/* Commercial Expert */}
              <div className="education-item mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className={`h6 mb-1 ${darkTheme ? "text-white" : "text-dark"}`}>
                      Commercial Expert with Tax Specialization - High School Diploma
                    </h3>
                    <p className={`small mb-0 ${darkTheme ? "text-light" : "text-muted"}`}>
                      Ramón L. Falcón - 2007
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Projects - Temporarily Hidden */}
          {/* 
          <section className="cv-section mb-5">
            <h2 className={`section-title h4 mb-4 ${darkTheme ? "text-white" : "text-dark"}`}>
              Featured Projects
            </h2>
            <div className="section-content">
              <div className="row g-4">
                
                Project Item Template
                <div className="col-md-6">
                  <div className={`card h-100 ${darkTheme ? "bg-dark border-secondary" : ""}`}>
                    <div className="card-body">
                      <h3 className={`h6 card-title ${darkTheme ? "text-white" : ""}`}>
                        [Project Name]
                      </h3>
                      <p className={`card-text small ${darkTheme ? "text-light" : "text-muted"}`}>
                        [Brief description of the project and your role]
                      </p>
                      <div className="project-tech mb-3">
                        <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white' }}>[Technology 1]</span>
                        <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white' }}>[Technology 2]</span>
                        <span className="badge me-1 mb-1" style={{ backgroundColor: '#0ea075', color: 'white' }}>[Technology 3]</span>
                      </div>
                      <div className="project-links">
                        <a href="[github-url]" className="btn btn-sm me-2" style={{ borderColor: '#0ea075', color: '#0ea075' }} target="_blank">
                          <i className="fab fa-github me-1"></i>Code
                        </a>
                        <a href="[demo-url]" className="btn btn-sm" style={{ backgroundColor: '#0ea075', borderColor: '#0ea075', color: 'white' }} target="_blank">
                          <i className="fas fa-external-link-alt me-1"></i>Live Demo
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                Add more project items as needed
              </div>
            </div>
          </section>
          */}

        </div>
      </main>

      {/* Custom Styles */}
      <style>{`
        .cv-layout {
          display: flex;
          min-height: 100vh;
          font-family: 'Poppins', sans-serif;
        }
        
        .cv-sidebar {
          width: 350px;
          position: fixed;
          height: 100vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          box-shadow: 2px 0 10px rgba(0,0,0,0.1);
        }
        
        .cv-main-content {
          flex: 1;
          margin-left: 350px;
          min-height: 100vh;
        }
        
        .profile-image {
          width: 160px;
          height: 160px;
          object-fit: cover;
          object-position: center top;
          border: 4px solid #0ea075;
          image-rendering: auto;
          image-rendering: -webkit-optimize-contrast;
          image-rendering: optimize-contrast;
          -ms-interpolation-mode: bicubic;
          max-width: none;
          flex-shrink: 0;
        }
        
        .contact-item {
          display: flex;
          align-items: center;
        }
        
        .cv-section {
          border-bottom: 1px solid ${darkTheme ? '#444' : '#eee'};
          padding-bottom: 2rem;
        }
        
        .cv-section:last-child {
          border-bottom: none;
        }
        
        .section-title {
          position: relative;
          padding-bottom: 0.5rem;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 50px;
          height: 3px;
          background: #0ea075;
        }
        
        @media (max-width: 991px) {
          .cv-sidebar {
            width: 100%;
            position: relative;
            height: auto;
          }
          
          .cv-main-content {
            margin-left: 0;
          }
          
          .cv-layout {
            flex-direction: column;
          }
        }
        
        @media (max-width: 768px) {
          .profile-image {
            width: 130px !important;
            height: 130px !important;
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: crisp-edges !important;
            image-rendering: high-quality !important;
          }
          
          .content-sections {
            padding: 2rem 1rem !important;
          }
        }
        
        /* Print Styles for PDF Export */
        @media print {
          .cv-layout {
            display: flex !important;
            min-height: auto !important;
            max-width: 100% !important;
            overflow: visible !important;
            background: #1e1e1e !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .cv-sidebar {
            width: 350px !important;
            position: static !important;
            height: auto !important;
            margin-bottom: 0 !important;
            box-shadow: none !important;
            page-break-inside: avoid;
            flex-shrink: 0;
            background: linear-gradient(135deg, #667eea, #764ba2) !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          .cv-main-content {
            margin-left: 0 !important;
            width: 100% !important;
            flex: 1;
            background: #1e1e1e !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Stronger text contrast for PDF */
          .cv-main-content h1,
          .cv-main-content h2, 
          .cv-main-content h3,
          .cv-main-content h4,
          .cv-main-content h5,
          .cv-main-content h6,
          .cv-main-content .text-white {
            color: #ffffff !important;
            font-weight: 700 !important;
            opacity: 1 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .cv-main-content p,
          .cv-main-content li,
          .cv-main-content .text-light,
          .cv-main-content .text-muted,
          .cv-main-content span,
          .cv-main-content .small {
            color: #ffffff !important;
            font-weight: 500 !important;
            opacity: 1 !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Específico para los links del sidebar en PDF */
          .cv-sidebar a,
          .cv-sidebar .contact-item span {
            color: #ffffff !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Social Icons originales restaurados */
          .social-icons {
            list-style: none;
            padding: 0;
            display: flex;
            justify-content: center;
            gap: 1rem;
          }

          .social-icons li {
            display: inline-block;
          }

          .social-icons a {
            color: white;
            text-decoration: none;
            display: inline-block;
            padding: 0.5rem;
          }

          /* MÁXIMO CONTRASTE PARA TEXTO PRINCIPAL */
          .cv-main-content *:not(.badge):not(.btn) {
            color: #ffffff !important;
            opacity: 1 !important;
            filter: none !important;
            -webkit-filter: none !important;
            text-shadow: none !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          .cv-main-content .badge {
            background-color: inherit !important;
            color: #ffffff !important;
            opacity: 1 !important;
          }

          /* Avoid page breaks in critical content */
          .cv-section {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .experience-item,
          .education-item {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 2rem;
          }

          .experience-item h3,
          .experience-item .badge,
          .experience-item ul {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .experience-item ul li {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          .tech-stack {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Sidebar content preservation */
          .profile-section,
          .skills-section,
          .languages-section {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 1.5rem;
          }
          
          .profile-section {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
            gap: 1rem !important;
          }
          
          .profile-image {
            width: 120px !important;
            height: 120px !important;
            object-fit: cover !important;
            object-position: center !important;
            image-rendering: -webkit-optimize-contrast !important;
            image-rendering: crisp-edges !important;
            image-rendering: high-quality !important;
          }
          
          .skills-pills {
            justify-content: flex-start !important;
          }
          
          .skill-pill {
            font-size: 10px !important;
            padding: 0.2rem 0.5rem !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Hide interactive elements in PDF */
          .d-print-none {
            display: none !important;
          }
          
          /* Ensure dark theme colors work in PDF */
          .dark-theme .cv-sidebar,
          .dark-theme .cv-main-content {
            background-color: #1a1a1a !important;
            color: #fff !important;
          }
          
          .dark-theme .card {
            background-color: #2a2a2a !important;
            border-color: #444 !important;
          }
        }
        
        /* Specific styles for PDF capture */
        #cv-content {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        /* Ensure proper rendering for PDF capture */
        .cv-layout * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        }
      `}</style>
    </div>
  );
};

export default CVLayout;